import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Button from "../../../components/atoms/button/Button";
import "./RequestFeedAssay.css";
import {
  getUserRole,
  formatUserDetails,
  submitFeedAssayRequest,
  getEditSampleData,
  formatEditSampleData,
  getUnits,
  getProductTrades,
  getProductsByTradeId,
  getFeedTypeByTradeAndProduct,
  getSpeciesByTradeAndProductAndFeed,
  getProductConfiguration
} from "./requestFeedAssayApi";
import SecondTabNavigation from "../../../components/organisms/SecondTabNavigation";
import PageDescriptionBar from "../../../components/atoms/pageDescriptionBar/PageDescriptionBar";

const tabConfig = [
  { label: "Request Feed Assay", path: "/request-feed-assay" },
];

const defaultSample = (id = 1) => ({
  id,
  expanded: true,
  productTradeName: "",
  productType: "",
  feedType: "",
  species: "",
  unit: "",
  cfrMinLevel: "",
  cfrMaxLevel: "",
  loqValue: "",
  expectedLevel: "",
  phibroLots: "",
  customerSampleId: "",
  referenceNo: "",
  manufactureDate: "",
  remarks: "",
  sampleId: 0,
  isProductTypeEnabled: false,
  isFeedTypeEnabled: false,
  isSpeciesEnabled: false,
  errors: {
    productTradeName: "",
    productType: "",
    feedType: "",
    species: "",
    unit: "",
    expectedLevel: "",
    phibroLots: "",
    customerSampleId: "",
  },
});

const RequestFeedAssay = () => {
  const [productTrades, setProductTrades] = useState([]);
  // per-sample option maps to avoid clearing options for other samples
  const [productTypeOptionsMap, setProductTypeOptionsMap] = useState({});
  const [feedTypeOptionsMap, setFeedTypeOptionsMap] = useState({});
  const [speciesOptionsMap, setSpeciesOptionsMap] = useState({});
  const [units, setUnits] = useState([]);
  const [samples, setSamples] = useState([defaultSample(1)]);
  const [userDetails, setUserDetails] = useState({
    email: "",
    name: "",
    roleName: [],
    companyName: "",
    companyId: null,
    locationName: "",
    locationId: null,
    salesRep: "",
    locations: []
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const sampleCounterRef = useRef(1);
  const [isEdit, setIsEdit] = useState(false);
  const [locationError, setLocationError] = useState("");
  const [validated, setValidated] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const urlParams = new URLSearchParams(location.search);
        const sampleId = urlParams.get("sampleId");

        const [userRes, unitsData, productTradesData] = await Promise.all([
          getUserRole(),
          getUnits(),
          getProductTrades()
        ]);

        const formattedUserData = formatUserDetails(userRes || {});
        setUserDetails(formattedUserData);
        setUnits(Array.isArray(unitsData) ? unitsData.filter(unit => unit && unit.id && unit.name) : []);
        setProductTrades(Array.isArray(productTradesData) ? productTradesData.filter(trade => trade && trade.productTradeNameId && trade.productTradeName) : []);

        if (sampleId) {
          setIsEdit(true);
          const editData = await getEditSampleData(sampleId);
          const formattedEditData = formatEditSampleData(editData);

          setUserDetails((prev) => ({
            ...prev,
            companyName: formattedEditData.companyName || prev.companyName,
            locationName: formattedEditData.locationName || prev.locationName,
            locationId: formattedEditData.locationId || prev.locationId,
            salesRep: formattedEditData.phibroRepName || prev.salesRep,
          }));

    if (formattedEditData.samples && formattedEditData.samples.length > 0) {
            const loadDependentData = async () => {
              const updatedSamples = [];
              for (const sample of formattedEditData.samples) {
                let updatedSample = { ...sample };
                try {
                  if (sample.productTradeName) {
                    const productTradeId = parseInt(sample.productTradeName);
                    const products = await getProductsByTradeId(productTradeId);
                    const validProducts = Array.isArray(products) ? products.filter(p => p && p.productTypeId && p.name) : [];
                    // set per-sample product type options
                    setProductTypeOptionsMap(prev => ({ ...prev, [sample.id]: validProducts }));
                    updatedSample.isProductTypeEnabled = true;
                    if (sample.productType && validProducts.length > 0) {
                      const productTypeId = parseInt(sample.productType);
                      const feeds = await getFeedTypeByTradeAndProduct(productTradeId, productTypeId);
                      const validFeeds = Array.isArray(feeds) ? feeds.filter(f => f && f.feedTypeId && f.feedTypeName) : [];
                      setFeedTypeOptionsMap(prev => ({ ...prev, [sample.id]: validFeeds }));
                      updatedSample.isFeedTypeEnabled = true;
                      if (sample.feedType && validFeeds.length > 0) {
                        const feedTypeId = parseInt(sample.feedType);
                        const speciesData = await getSpeciesByTradeAndProductAndFeed(productTradeId, productTypeId, feedTypeId);
                        const validSpecies = Array.isArray(speciesData) ? speciesData.filter(s => s && s.speciesId && s.speciesName) : [];
                        setSpeciesOptionsMap(prev => ({ ...prev, [sample.id]: validSpecies }));
                        updatedSample.isSpeciesEnabled = true;
                        if (sample.species && validSpecies.length > 0) {
                          const speciesId = parseInt(sample.species);
                          const selectedSpecies = validSpecies.find(s => s.speciesId === speciesId);
                          if (selectedSpecies && selectedSpecies.masterSpeciesId) {
                            const config = await getProductConfiguration(
                              productTradeId,
                              productTypeId,
                              feedTypeId,
                              selectedSpecies.masterSpeciesId
                            );
                            updatedSample = {
                              ...updatedSample,
                              cfrMinLevel: config?.cfrMinLevel || "",
                              cfrMaxLevel: config?.cfrMaxLevel || "",
                              loqValue: config?.loqValue || "",
                            };
                          }
                        }
                      }
                    }
                  }
                } catch (apiError) { }
                updatedSamples.push(updatedSample);
              }
              setSamples(updatedSamples);
            };
            await loadDependentData();
          } else {
            setSamples([defaultSample(1)]);
          }
        }
      } catch (err) {
        setUnits([]);
        setProductTrades([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [location.search]);

  const validateField = (sampleId, fieldName, value) => {
    let errorMessage = "";
    switch (fieldName) {
      case "productTradeName":
        if (!value) errorMessage = "Product Trade Name is required";
        break;
      case "productType":
        if (!value) errorMessage = "Product Type is required";
        break;
      case "feedType":
        if (!value) errorMessage = "Feed Type is required";
        break;
      case "species":
        if (!value) errorMessage = "Species is required";
        break;
      case "unit":
        if (!value) errorMessage = "Unit is required";
        break;
      case "expectedLevel":
        if (!value) {
          errorMessage = "Expected Level is required";
        }
        break;
      case "phibroLots":
        if (!value) errorMessage = "Phibro Lots is required";
        break;
      case "customerSampleId":
        if (!value) errorMessage = "Customer Sample ID is required";
        break;
      default: break;
    }
    return errorMessage;
  };

  const validateLocation = (locationName) => {
    if (!locationName) {
      setLocationError("Location is required");
      return false;
    }
    setLocationError("");
    return true;
  };

    
    const userRole = JSON.parse(localStorage.getItem("currentRole"));
  const refreshUserData = async () => {
    try {
      const userRes = await getUserRole();
      const refreshedUserData = formatUserDetails(userRes || {});
      const currentLocationId = userDetails.locationId;
      const currentLocationName = userDetails.locationName;
      setUserDetails(prev => ({
        ...prev,
        ...refreshedUserData,
        locationName: currentLocationName || refreshedUserData.locationName,
        locationId: currentLocationId || refreshedUserData.locationId
      }));
      const finalUserData = {
        ...refreshedUserData,
        locationName: currentLocationName || refreshedUserData.locationName,
        locationId: currentLocationId || refreshedUserData.locationId
      };
      return finalUserData;
    } catch (error) {
      return userDetails;
    }
  };

  const handleAddSample = () => {
    const nextId = ++sampleCounterRef.current;
    setSamples((prev) => [...prev, defaultSample(nextId)]);
  };

  const handleRemoveSample = (id) =>
    setSamples((prev) => prev.filter((s) => s.id !== id));

  const handleExpandToggle = (id) =>
    setSamples((prev) =>
      prev.map((s) => (s.id === id ? { ...s, expanded: !s.expanded } : s))
    );

  const handleSampleChange = async (id, field, value, speciesListOverride) => {
    const errorMessage = validateField(id, field, value);
    setSamples((prev) =>
      prev.map((s) => {
        if (s.id === id) {
          let updatedSample = {
            ...s,
            [field]: value,
            errors: {
              ...s.errors,
              [field]: errorMessage
            }
          };
              if (field === "productTradeName") {
            updatedSample = {
              ...updatedSample,
              productType: "",
              feedType: "",
              species: "",
              cfrMinLevel: "",
              cfrMaxLevel: "",
              loqValue: "",
              isProductTypeEnabled: !!value,
              isFeedTypeEnabled: false,
              isSpeciesEnabled: false,
              errors: {
                ...updatedSample.errors,
                productType: "",
                feedType: "",
                species: ""
              }
            };
                  if (value) fetchProductsByTradeId(parseInt(value), id);
                  else {
                    // clear options for this sample only
                    setProductTypeOptionsMap(prev => ({ ...prev, [id]: [] }));
                    setFeedTypeOptionsMap(prev => ({ ...prev, [id]: [] }));
                    setSpeciesOptionsMap(prev => ({ ...prev, [id]: [] }));
                  }
          } else if (field === "productType") {
            updatedSample = {
              ...updatedSample,
              feedType: "",
              species: "",
              cfrMinLevel: "",
              cfrMaxLevel: "",
              loqValue: "",
              isFeedTypeEnabled: !!value && !!updatedSample.productTradeName,
              isSpeciesEnabled: false,
              errors: {
                ...updatedSample.errors,
                feedType: "",
                species: ""
              }
            };
                  if (value && updatedSample.productTradeName)
                    fetchFeedTypeByTradeAndProduct(parseInt(updatedSample.productTradeName), parseInt(value), id);
                  else {
                    setFeedTypeOptionsMap(prev => ({ ...prev, [id]: [] }));
                    setSpeciesOptionsMap(prev => ({ ...prev, [id]: [] }));
                  }
          } else if (field === "feedType") {
            updatedSample = {
              ...updatedSample,
              species: "",
              cfrMinLevel: "",
              cfrMaxLevel: "",
              loqValue: "",
              isSpeciesEnabled: !!value && !!updatedSample.productTradeName && !!updatedSample.productType,
              errors: {
                ...updatedSample.errors,
                species: ""
              }
            };
            if (value && updatedSample.productTradeName && updatedSample.productType)
                  if (value && updatedSample.productTradeName && updatedSample.productType)
                    fetchSpeciesByTradeAndProductAndFeed(parseInt(updatedSample.productTradeName), parseInt(updatedSample.productType), parseInt(value), id);
                  else setSpeciesOptionsMap(prev => ({ ...prev, [id]: [] }));
            updatedSample = {
              ...updatedSample,
              cfrMinLevel: "",
              cfrMaxLevel: "",
              loqValue: ""
            };
            // if (value && updatedSample.productTradeName && updatedSample.productType && updatedSample.feedType)
            //   fetchProductConfiguration(parseInt(updatedSample.productTradeName), parseInt(updatedSample.productType), parseInt(updatedSample.feedType), parseInt(value), id);
          } else if (field === "species") {
            if (value && updatedSample.productTradeName && updatedSample.productType && updatedSample.feedType) {
              fetchProductConfiguration(
                parseInt(updatedSample.productTradeName),
                parseInt(updatedSample.productType),
                parseInt(updatedSample.feedType),
                parseInt(value),
                id,
                speciesListOverride
              );
            }
          }
          return updatedSample;
        }
        return s;
      })
    );
  };

  const fetchProductsByTradeId = async (tradeId, sampleId) => {
    try {
      if (!tradeId || isNaN(tradeId)) return;
      const products = await getProductsByTradeId(tradeId);
      const valid = Array.isArray(products) ? products.filter(type => type && type.productTypeId && type.name) : [];
      setProductTypeOptionsMap(prev => ({ ...prev, [sampleId]: valid }));
    } catch (error) {
      setProductTypeOptionsMap(prev => ({ ...prev, [sampleId]: [] }));
    }
  };

  const fetchFeedTypeByTradeAndProduct = async (tradeId, productId, sampleId) => {
    try {
      if (!tradeId || !productId || isNaN(tradeId) || isNaN(productId)) return;
      const feeds = await getFeedTypeByTradeAndProduct(tradeId, productId);
      const valid = Array.isArray(feeds) ? feeds.filter(feed => feed && feed.feedTypeId && feed.feedTypeName) : [];
      setFeedTypeOptionsMap(prev => ({ ...prev, [sampleId]: valid }));

      // Auto-select feed type with defaultSelection flag
      const defaultFeed = valid.find((f) => f.defaultSelection);
      if (defaultFeed) {
        handleSampleChange(sampleId, "feedType", String(defaultFeed.feedTypeId));
      }
    } catch (error) {
      setFeedTypeOptionsMap(prev => ({ ...prev, [sampleId]: [] }));
    }
  };

  const fetchSpeciesByTradeAndProductAndFeed = async (tradeId, productId, feedId, sampleId) => {
    try {
      if (!tradeId || !productId || !feedId || isNaN(tradeId) || isNaN(productId) || isNaN(feedId)) return;
      const speciesData = await getSpeciesByTradeAndProductAndFeed(tradeId, productId, feedId);
      const valid = Array.isArray(speciesData) ? speciesData.filter(s => s && s.speciesId && s.speciesName) : [];
      setSpeciesOptionsMap(prev => ({ ...prev, [sampleId]: valid }));

      // Auto-select species with defaultSelection flag
      const defaultSpecies = valid.find((s) => s.defaultSelection);
      if (defaultSpecies) {
        handleSampleChange(sampleId, "species", String(defaultSpecies.speciesId), valid);
      }
    } catch (error) {
      setSpeciesOptionsMap(prev => ({ ...prev, [sampleId]: [] }));
    }
  };

  const fetchProductConfiguration = async (tradeId, productId, feedId, speciesId, sampleId, speciesListOverride) => {
    try {
      if (!tradeId || !productId || !feedId || !speciesId || isNaN(tradeId) || isNaN(productId) || isNaN(feedId) || isNaN(speciesId)) return;
      const speciesList = speciesListOverride || speciesOptionsMap[sampleId] || [];
      const selectedSpecies = speciesList.find((s) => s && String(s.speciesId) === String(speciesId));
      if (!selectedSpecies || !selectedSpecies.masterSpeciesId) return;
      const config = await getProductConfiguration(
        tradeId, productId, feedId, selectedSpecies.masterSpeciesId
      );
      setSamples((prev) =>
        prev.map((s) =>
          s.id === sampleId
            ? {
              ...s,
              cfrMinLevel: config?.cfrMinLevel || "",
              cfrMaxLevel: config?.cfrMaxLevel || "",
              loqValue: config?.loqValue || "",
            }
            : s
        )
      );
    } catch (error) { }
  };

  const handleLocationChange = (e) => {
    const selectedLocationName = e.target.value;
    const selectedLocation = userDetails.locations?.find(
      loc => loc && loc.locationName === selectedLocationName
    );
    setUserDetails(prev => ({
      ...prev,
      locationName: selectedLocationName,
      locationId: selectedLocation?.locationId || null,
      salesRep: selectedLocation?.salesRep || ""
    }));
    validateLocation(selectedLocationName);
  };

  const validateAllFields = () => {
    let isValid = true;
     
    // Validate location
    if (!validateLocation(userDetails.locationName)) {
      isValid = false;
    }
    if (!userDetails.locationId) {
      isValid = false;
    }

    // Validate samples synchronously
    const updatedSamples = samples.map((sample) => {
        const newErrors = {};
      const requiredFields = ['productTradeName', 'productType', 'feedType', 'species', 'unit', 'expectedLevel', 'phibroLots', 'customerSampleId'];
      
      requiredFields.forEach(field => {
          const errorMessage = validateField(sample.id, field, sample[field]);
          newErrors[field] = errorMessage;
        if (errorMessage) {
          isValid = false;
        }
        });
        return { ...sample, errors: newErrors };
    });

    // Update state with validation errors for display
    setSamples(updatedSamples);
    setValidated(true);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // useRef guard is synchronous — blocks double-clicks even before React re-renders
    if (submittingRef.current) return;
    if (!validateAllFields()) {
      toast.error("Please fill in all required fields.");
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const currentUserData = await refreshUserData();
      const submissionUserData = {
        ...currentUserData,
        locationId: userDetails.locationId || currentUserData.locationId,
        locationName: userDetails.locationName || currentUserData.locationName
      };
      const response = await submitFeedAssayRequest(
        submissionUserData,
        samples,
        isEdit
      );
      toast.success("Submitted successfully!");
      // Keep button disabled through the navigation delay so the user cannot re-submit
      setTimeout(() => {
        let path= "";
        if( userRole === "Customer"){
          path= `/request-feed-assay/print-preview?groupId=${response.groupId}`
        } else if(userRole === "Lab Admin"){
            path= `/company-management/print-preview?groupId=${response.groupId}`
          } else {
            path= `/analyst-companyManagement/print-preview?groupId=${response.groupId}`
          }
        navigate(path, {
        state: { samples, userDetails: submissionUserData, groupId: response.groupId },
          }
        );
      }, 6000);
    } catch (error) {
      toast.error("Failed to submit request. Please try again.");
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleNumericInput = (e, sampleId) => {
    const value = e.target.value;
    const numericRegex = /^[0-9]*\.?[0-9]*$/;
    const maxDigitsBeforeDecimal = /^\d{0,4}(\.\d{0,4})?$/;
    if (numericRegex.test(value) && maxDigitsBeforeDecimal.test(value)) {
      handleSampleChange(sampleId, "expectedLevel", value);
    }
  };

  const handleKeyPress = (e) => {
    const charCode = e.which ? e.which : e.keyCode;
    const char = String.fromCharCode(charCode);
    const currentValue = e.target.value;
    if ([8, 9, 27, 13, 46].indexOf(charCode) !== -1 ||
      (charCode === 65 && e.ctrlKey) ||
      (charCode === 67 && e.ctrlKey) ||
      (charCode === 86 && e.ctrlKey) ||
      (charCode === 88 && e.ctrlKey)) {
      return;
    }
    if (!/[0-9.]/.test(char)) {
      e.preventDefault();
      return;
    }
    if (char === '.' && currentValue.indexOf('.') !== -1) {
      e.preventDefault();
      return;
    }
    const beforeDecimal = currentValue.split('.')[0] || '';
    const afterDecimal = currentValue.split('.')[1] || '';
    if (char === '.') {
      if (beforeDecimal.length > 4) {
        e.preventDefault();
      }
    } else if (currentValue.indexOf('.') === -1) {
      if (beforeDecimal.length >= 4) {
        e.preventDefault();
      }
    } else {
      if (afterDecimal.length >= 4) {
        e.preventDefault();
      }
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "40px" }}>
        <Spinner animation="border" /> Loading...
      </div>
    );
  }

  return (
    <div>
      <ToastContainer position="top-right" autoClose={1000} />
      {userRole === "Customer" && <SecondTabNavigation tabConfig={tabConfig} />}
      <PageDescriptionBar items={[
        { type: "icon", icon: "➕", text: "Add Sample – Append a new sample row to the request" },
        { type: "icon", icon: "🗑", text: "Delete Sample – Remove a sample from the current request" },
        { type: "separator" },
        { type: "icon", icon: "📤", text: "Submit – Send the completed feed assay request to the lab" },
        { type: "icon", icon: "✏️", text: "Edit Mode – Modify details of a previously submitted request" },
      ]} />
      <div className="company-info">
        <div className="company-info-label">Company Information</div>
        <form className={`full-width-form ${validated ? 'was-validated' : ''}`} noValidate>
          <div className="form-row">
            <div className="field">
              <label>Company Name</label>
              <select value={userDetails.companyName || ""} disabled title="Company name">
                <option value={userDetails.companyName || ""}>
                  {userDetails.companyName || "No Company Selected"}
                </option>
              </select>
            </div>
            <div className="field">
              <label>
                Location <span className="required-star">*</span>
              </label>
              <select
                value={userDetails.locationName || ""}
                onChange={handleLocationChange}
                className={locationError ? 'is-invalid' : ''}
                required
                title="Select location"
              >
                <option value="">Select Location</option>
                {(userDetails.locations || [])
                  .filter(location => location && location.locationId && location.locationName)
                  .map(location => (
                    <option key={location.locationId} value={location.locationName}>
                      {location.locationName}
                    </option>
                  ))}
              </select>
              {locationError && (
                <div className="invalid-feedback d-block">
                  {locationError}
                </div>
              )}
            </div>
            <div className="field">
              <label>Sales Rep</label>
              <select value={userDetails.salesRep || ""} disabled title="Sales representative">
                <option value={userDetails.salesRep || ""}>
                  {userDetails.salesRep || "No Sales Rep Selected"}
                </option>
              </select>
            </div>
          </div>
        </form>
      </div>
      <div className="customer-sample">
        {(samples || []).map((sample, idx) => {
          if (!sample || typeof sample !== 'object') {
            return null;
          }
          return (
            <div key={sample.id} className="sample-block">
              <div className="sample-header-bar">
                <span className="sample-title">
                  {idx + 1}. Customer Sample ID:{" "}
                  {sample.customerSampleId || `0${idx + 1}`}
                </span>
                <span className="sample-actions">
                  <button
                    type="button"
                    title={sample.expanded ? "Collapse sample" : "Expand sample"}
                    onClick={() => handleExpandToggle(sample.id)}>
                    {sample.expanded ? "▲" : "▼"}
                  </button>
                  {samples.length > 1 && (
                    <>
                      <span className="divider"></span>
                      <button
                        type="button"
                        onClick={() => handleRemoveSample(sample.id)}
                        title="Remove sample"
                      >
                        ✕
                      </button>
                    </>
                  )}
                </span>
              </div>
              {sample.expanded && (
                <form className={`full-width-form mt-16 ${validated ? 'was-validated' : ''}`} noValidate>
                  <div className="form-row">
                    <div className="field">
                      <label>
                        Product Trade Name <span className="required-star">*</span>
                      </label>
                      <select
                        value={sample.productTradeName || ""}
                        onChange={(e) =>
                          handleSampleChange(
                            sample.id,
                            "productTradeName",
                            e.target.value
                          )
                        }
                        className={sample.errors?.productTradeName ? 'is-invalid' : ''}
                        required
                        title="Select product trade name"
                      >
                        <option value="">Select Product Trade Name</option>
                        {productTrades
                          .filter(trade => trade && trade.productTradeNameId && trade.productTradeName)
                          .map((trade) => (
                            <option
                              key={trade.productTradeNameId}
                              value={trade.productTradeNameId}
                            >
                              {trade.productTradeName}
                            </option>
                          ))}
                      </select>
                      {sample.errors?.productTradeName && (
                        <div className="invalid-feedback d-block">
                          {sample.errors.productTradeName}
                        </div>
                      )}
                    </div>
                    <div className="field">
                      <label>
                        Product Type <span className="required-star">*</span>
                      </label>
                      <select
                        value={sample.productType || ""}
                        disabled={!sample.isProductTypeEnabled}
                        onChange={(e) =>
                          handleSampleChange(
                            sample.id,
                            "productType",
                            e.target.value
                          )
                        }
                        className={sample.errors?.productType ? 'is-invalid' : ''}
                        required
                        title="Select product type"
                      >
                        <option value="">Select Product Type</option>
                        {(productTypeOptionsMap[sample.id] || [])
                          .filter(type => type && type.productTypeId && type.name)
                          .map((type) => (
                            <option key={type.productTypeId} value={type.productTypeId}>
                              {type.name}
                            </option>
                          ))}
                      </select>
                      {sample.errors?.productType && (
                        <div className="invalid-feedback d-block">
                          {sample.errors.productType}
                        </div>
                      )}
                    </div>
                    <div className="field">
                      <label>
                        Feed Type <span className="required-star">*</span>
                      </label>
                      <select
                        value={sample.feedType || ""}
                        disabled={!sample.isFeedTypeEnabled}
                        onChange={(e) =>
                          handleSampleChange(sample.id, "feedType", e.target.value)
                        }
                        className={sample.errors?.feedType ? 'is-invalid' : ''}
                        required
                        title="Select feed type"
                      >
                        <option value="">Select Feed Type</option>
                        {(feedTypeOptionsMap[sample.id] || [])
                          .filter(feed => feed && feed.feedTypeId && feed.feedTypeName)
                          .map((feed) => (
                            <option key={feed.feedTypeId} value={feed.feedTypeId}>
                              {feed.feedTypeName}
                            </option>
                          ))}
                      </select>
                      {sample.errors?.feedType && (
                        <div className="invalid-feedback d-block">
                          {sample.errors.feedType}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="field">
                      <label>
                        Species <span className="required-star">*</span>
                      </label>
                      <select
                        value={sample.species || ""}
                        disabled={!sample.isSpeciesEnabled}
                        onChange={(e) =>
                          handleSampleChange(sample.id, "species", e.target.value)
                        }
                        className={sample.errors?.species ? 'is-invalid' : ''}
                        required
                        title="Select species"
                      >
                        <option value="">Select Species</option>
                        {(speciesOptionsMap[sample.id] || [])
                          .filter(s => s && s.speciesId && s.speciesName)
                          .map((s) => (
                            <option key={s.speciesId} value={s.speciesId}>
                              {s.speciesName}
                            </option>
                          ))}
                      </select>
                      {sample.errors?.species && (
                        <div className="invalid-feedback d-block">
                          {sample.errors.species}
                        </div>
                      )}
                    </div>
                    <div className="field">
                      <label>
                        Unit <span className="required-star">*</span>
                      </label>
                      <select
                        value={sample.unit || ""}
                        onChange={(e) =>
                          handleSampleChange(sample.id, "unit", e.target.value)
                        }
                        className={sample.errors?.unit ? 'is-invalid' : ''}
                        required
                        title="Select unit"
                      >
                        <option value="">Select Unit</option>
                        {units
                          .filter(unit => unit && unit.id && unit.name)
                          .map((unit) => (
                            <option key={unit.id} value={unit.id}>
                              {unit.name}
                            </option>
                          ))}
                      </select>
                      {sample.errors?.unit && (
                        <div className="invalid-feedback d-block">
                          {sample.errors.unit}
                        </div>
                      )}
                    </div>
                    <div className="field">
                      <label>CFR Min Level</label>
                      <input
                        value={sample.cfrMinLevel || ""}
                        readOnly
                        disabled
                        title="CFR minimum level"
                        style={{ backgroundColor: "#f8f9fa" }}
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="field">
                      <label>CFR Max Level</label>
                      <input
                        value={sample.cfrMaxLevel || ""}
                        readOnly
                        disabled
                        title="CFR maximum level"
                        style={{ backgroundColor: "#f8f9fa" }}
                      />
                    </div>
                    <div className="field">
                      <label>LOQ Value</label>
                      <input
                        value={sample.loqValue || ""}
                        readOnly
                        disabled
                        title="LOQ value"
                        style={{ backgroundColor: "#f8f9fa" }}
                      />
                    </div>
                    <div className="field">
                      <label>
                        Expected Level <span className="required-star">*</span>
                      </label>
                      <input
                        type="text"
                        value={sample.expectedLevel || ""}
                         onChange={(e) => handleSampleChange(sample.id, "expectedLevel", e.target.value)}
                        title="Enter expected level"
                        className={sample.errors?.expectedLevel ? 'is-invalid' : ''}
                        required
                      />
                      {sample.errors?.expectedLevel && (
                        <div className="invalid-feedback d-block">
                          {sample.errors.expectedLevel}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="field">
                      <label>Phibro Lot No. <span className="required-star">*</span></label>
                      <input
                        value={sample.phibroLots || ""}
                        onChange={(e) =>
                          handleSampleChange(
                            sample.id,
                            "phibroLots",
                            e.target.value
                          )
                        }
                        className={sample.errors?.phibroLots ? 'is-invalid' : ''}
                        required
                        title="Enter Phibro lot number"
                      />
                      {sample.errors?.phibroLots && (
                        <div className="invalid-feedback d-block">
                          {sample.errors.phibroLots}
                        </div>
                      )}
                    </div>
                    <div className="field">
                      <label>
                        Customer Sample ID <span className="required-star">*</span>
                      </label>
                      <input
                        value={sample.customerSampleId || ""}
                        onChange={(e) =>
                          handleSampleChange(
                            sample.id,
                            "customerSampleId",
                            e.target.value
                          )
                        }
                        className={sample.errors?.customerSampleId ? 'is-invalid' : ''}
                        required
                        title="Enter customer sample ID"
                      />
                      {sample.errors?.customerSampleId && (
                        <div className="invalid-feedback d-block">
                          {sample.errors.customerSampleId}
                        </div>
                      )}
                    </div>
                    <div className="field">
                      <label>Reference No</label>
                      <input
                        value={sample.referenceNo || ""}
                        onChange={(e) =>
                          handleSampleChange(
                            sample.id,
                            "referenceNo",
                            e.target.value
                          )
                        }
                        title="Enter reference number"
                      />
                    </div>
                    <div className="field">
                      <label>Manufacture Date</label>
                      <input
                        type="date"
                        value={sample.manufactureDate || ""}
                        max={new Date().toISOString().split("T")[0]}
                        onChange={(e) => {
                          const val = e.target.value;
                          handleSampleChange(
                            sample.id,
                            "manufactureDate",
                            val || null
                          );
                        }}
                      />
                    </div>
                  </div>
                  <div className="field">
                    <label>Remarks</label>
                    <textarea
                      value={sample.remarks || ""}
                      onChange={(e) =>
                        handleSampleChange(sample.id, "remarks", e.target.value)
                      }
                      rows="4"
                      style={{ resize: "vertical", minHeight: "80px" }}
                      placeholder="Enter any additional remarks here..."
                      title="Enter remarks"
                    />
                  </div>
                </form>
              )}
            </div>
          );
        })}
        <div className="actions-row">
          <Button variant="outlined" onClick={handleAddSample} title="Add another sample to the request">
            Add More Sample
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            title="Submit feed assay request"
            disabled={submitting}
          >
            {submitting ? "Submitting..." : "Submit"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RequestFeedAssay;